import { isServer } from '@moonpig/web-core-utils'
import { createMetrics, logger } from '@moonpig/web-core-monitoring'
import type { BanditVariation } from '@eppo/js-client-sdk-common'
import {
  EppoBanditModels,
  EppoBanditVariations,
  EppoConfiguration,
} from '../../types'
import { createEppoClient } from '../../browser'

const EPPO_REFRESH_INTERVAL = 30000
const EPPO_TIMEOUT = 1000

let eppoConfiguration: EppoConfiguration | undefined
// The contentfulConfiguration only exists to query the experiments tagged with `Contentful`.
// This seems the only reliable way of understanding which experiments to pass through
// automatically to the content-api. It is not used outside of `getContentfulExperimentNames`.
//
// A query has been raised with Eppo around being able to see experiment tags without needing to do
// the above.
let contentfulConfiguration: EppoConfiguration | undefined
let banditVariations: EppoBanditVariations = {}
let banditModels: EppoBanditModels = {}
let lastFetch: Date

type ClientEppoConfig = {
  eppoConfiguration: EppoConfiguration
  contentfulConfiguration: EppoConfiguration
  banditVariations: EppoBanditVariations
  banditModels: EppoBanditModels
}

let cachedClientEppoConfig: ClientEppoConfig | undefined

export const setClientEppoConfig = (config: ClientEppoConfig) => {
  cachedClientEppoConfig = config
}

export const getClientEppoConfig = () => cachedClientEppoConfig

type BanditReference = {
  modelVersion: string
  flagVariations: BanditVariation[]
}

type FetchConfigResult = {
  flags: EppoConfiguration
  banditVariations: EppoBanditVariations
}

type GetEppoClientOptions = {
  isBot: boolean
}

export const getEppoClient = async ({ isBot }: GetEppoClientOptions) => {
  if (isServer) {
    const shouldFetch = lastFetch
      ? new Date().getTime() - lastFetch.getTime() >= EPPO_REFRESH_INTERVAL
      : true

    if (shouldFetch) {
      const [mainConfig, contentfulConfig] = await Promise.all([
        fetchConfigWithBandits(process.env.EPPO_API_SDK_KEY),
        fetchEppoConfiguration(process.env.EPPO_CONTENTFUL_API_SDK_KEY),
      ])

      eppoConfiguration = mainConfig.flags
      banditVariations = mainConfig.banditVariations
      contentfulConfiguration = contentfulConfig

      if (Object.keys(banditVariations).length > 0) {
        banditModels = await fetchBanditModels(process.env.EPPO_API_SDK_KEY)
      } else {
        banditModels = {}
      }
    }
  } else {
    const cached = getClientEppoConfig()
    if (cached) {
      eppoConfiguration = cached.eppoConfiguration
      contentfulConfiguration = cached.contentfulConfiguration
      banditVariations = cached.banditVariations
      banditModels = cached.banditModels
    }
  }

  const getContentfulExperimentNames = () => {
    return Object.values(contentfulConfiguration ?? {}).reduce(
      (acc, flag) => (flag.enabled ? [...acc, flag.key] : acc),
      [] as string[],
    )
  }

  const client = createEppoClient({
    flagsConfiguration: eppoConfiguration ?? null,
    banditVariations,
    banditModels,
    isBot,
  })

  return {
    client,
    eppoConfiguration: eppoConfiguration ?? {},
    banditVariations,
    banditModels,
    getContentfulExperimentNames,
  }
}

const indexBanditVariationsByFlagKey = (
  banditReferences: Record<string, BanditReference>,
): EppoBanditVariations => {
  return Object.values(banditReferences).reduce<EppoBanditVariations>(
    (acc, banditReference) => {
      banditReference.flagVariations.forEach(banditVariation => {
        if (!acc[banditVariation.flagKey]) {
          acc[banditVariation.flagKey] = []
        }
        acc[banditVariation.flagKey].push(banditVariation)
      })
      return acc
    },
    {},
  )
}

const fetchConfigWithBandits = async (
  apiKey: string | undefined,
): Promise<FetchConfigResult> => {
  const eppoUrl = new URL('https://fscdn.eppo.cloud/api/flag-config/v1/config')
  eppoUrl.search = new URLSearchParams({
    apiKey: `${apiKey}`,
    sdkName: 'node-server-sdk',
    sdkVersion: '3.5.0',
  }).toString()

  const abortController = new AbortController()
  const timerId = setTimeout(() => {
    logger.fixToday(`EPPO reached timeout of ${EPPO_TIMEOUT}ms`)
    abortController.abort()
  }, EPPO_TIMEOUT)
  const { traceAsync, count } = createMetrics({ useTracing: false })

  try {
    const res = await traceAsync(
      { metricName: 'fetch-eppo-configuration' },
      async () => fetch(eppoUrl, { signal: abortController.signal }),
    )

    if (!res.ok) {
      throw new Error(`Failed to fetch EPPO configuration: ${res.statusText}`)
    }

    const json = await res.json()

    if (!json.flags) {
      throw new Error('Failed to fetch EPPO configuration: no flags found')
    }

    lastFetch = new Date()
    return {
      flags: json.flags,
      banditVariations: indexBanditVariationsByFlagKey(
        json.banditReferences ?? {},
      ),
    }
  } catch (error) {
    logger.fixToday('Failed to fetch EPPO configuration', error)
    if (!eppoConfiguration) {
      count({ metricName: 'empty-eppo-configuration' })
      return { flags: {}, banditVariations: {} }
    }
    return { flags: eppoConfiguration, banditVariations }
  } finally {
    clearTimeout(timerId)
  }
}

const fetchEppoConfiguration = async (apiKey: string | undefined) => {
  const eppoUrl = new URL('https://fscdn.eppo.cloud/api/flag-config/v1/config')
  eppoUrl.search = new URLSearchParams({
    apiKey: `${apiKey}`,
    sdkName: 'node-server-sdk',
    sdkVersion: '3.5.0',
  }).toString()

  const abortController = new AbortController()
  const timerId = setTimeout(() => {
    logger.fixToday(`EPPO reached timeout of ${EPPO_TIMEOUT}ms`)
    abortController.abort()
  }, EPPO_TIMEOUT)
  const { traceAsync, count } = createMetrics({ useTracing: false })

  try {
    const res = await traceAsync(
      { metricName: 'fetch-eppo-configuration' },
      async () => fetch(eppoUrl, { signal: abortController.signal }),
    )

    if (!res.ok) {
      throw new Error(`Failed to fetch EPPO configuration: ${res.statusText}`)
    }

    const json = await res.json()

    if (!json.flags) {
      throw new Error('Failed to fetch EPPO configuration: no flags found')
    }

    lastFetch = new Date()
    return json.flags
  } catch (error) {
    logger.fixToday('Failed to fetch EPPO configuration', error)
    if (!eppoConfiguration) {
      count({ metricName: 'empty-eppo-configuration' })
      return {}
    }
    return eppoConfiguration
  } finally {
    clearTimeout(timerId)
  }
}

const fetchBanditModels = async (
  apiKey: string | undefined,
): Promise<EppoBanditModels> => {
  const eppoUrl = new URL('https://fscdn.eppo.cloud/api/flag-config/v1/bandits')
  eppoUrl.search = new URLSearchParams({
    apiKey: `${apiKey}`,
    sdkName: 'node-server-sdk',
    sdkVersion: '3.5.0',
  }).toString()

  const abortController = new AbortController()
  const timerId = setTimeout(() => abortController.abort(), EPPO_TIMEOUT)

  try {
    const res = await fetch(eppoUrl, { signal: abortController.signal })

    if (!res.ok) {
      throw new Error(`Failed to fetch EPPO bandit models: ${res.statusText}`)
    }

    const json = await res.json()

    if (!json.bandits) {
      throw new Error('Failed to fetch EPPO bandit models: no bandits found')
    }

    return json.bandits
  } catch (error) {
    logger.fixToday('Failed to fetch EPPO bandit models', error)
    return banditModels
  } finally {
    clearTimeout(timerId)
  }
}
